import * as React from 'react';
import Layout from "../../../../Components/layouts";
import { Grid, MenuItem, Tabs, Tab, TextField } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import AppLoader from '../../../../Components/AppLoader';
import { RouteSkeleton } from '../../../../Components/RouteTransition';
import { interactiveCardStyles, pageEyebrowStyles, pageTitleTextStyles, surfaceCardStyles } from '../../../../theme/commonStyles';
import { makeStyles } from '@material-ui/core/styles';
import CourseIframeToolbar from '../../../../Components/CourseIframeToolbar';
import { SkillsAndTransformation } from 'Services/SkillsAndTransformation';
import { CoursesServices } from 'Services/CoursesServices';
import { getSSOAuthUrl } from 'Services/AuthService';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import LazyCardMedia from 'Components/LazyCardMedia';
import Typography from '@material-ui/core/Typography';
import { Button } from '@material-ui/core';
import { BorderLinearProgress, BorderLinearProgressComplete } from 'Pages/UserAdmin/components/DynamicCard';
import { useHistory, useLocation } from "react-router-dom";
import { notify } from 'utils';
import { UserPath } from 'Routes/routePaths';
import { useAuthState } from 'Context';
import { DEFAULT_COURSE_IMAGE_URL, USER_ROLES, shouldOpenCourseInNewTab } from 'utils/constants';
import { useImpersonation } from 'hooks/useImpersonation';
import { isImpersonating } from 'Services/impersonationSession';
import {
    courseCardLaunchLabel,
    courseLaunchUrl,
    OPEN_COURSE_LINK_NOTE,
} from 'utils/courseLaunch';
import { useMoodleCourseIframeGuard } from 'hooks/useMoodleCourseIframeGuard';
import { useCourseIframeSelection } from 'hooks/useCourseIframeSelection';
import {
    isLearningHubAvailable,
    getImpersonationLearningHubAvailable,
    getLearningHubAccess,
    setLearningHubAccess,
    learningHubMenuLabel,
    LEARNING_HUB_ACCESS_EVENT,
    LEARNING_HUB_AVAILABILITY_EVENT,
} from 'utils/learningHubAvailability';
import LearningHubAccessStrip from '../../../../Components/LearningHubAccessStrip';


const skillsAndTransformation = new SkillsAndTransformation();
const courseServices = new CoursesServices();
const COURSE_PAGE_SIZES = [12, 24, 48];

const courseMatchesSearch = (query, fields) => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    return fields.some((field) => String(field || '').toLowerCase().includes(needle));
};

const sliceCoursePage = (items, page, perpage) => {
    const size = COURSE_PAGE_SIZES.includes(perpage) ? perpage : 12;
    const safePage = Number.isFinite(page) && page > 0 ? page : 0;
    const start = safePage * size;
    return {
        items: items.slice(start, start + size),
        total: items.length,
        totalPages: items.length ? Math.ceil(items.length / size) : 0,
        start,
    };
};

export const calculatePercentage = (val, total) => {
    let percentage = parseFloat((val / total)) * 100;
    return percentage ? Math.floor(percentage) : 0;
}

const getCourseImageUrl = (course) =>
    (course?.imageUrl || course?.thumbnail_url || '').trim() || DEFAULT_COURSE_IMAGE_URL;

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        marginBottom: theme.spacing(2.5),
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 640,
    },
    emptyState: {
        color: '#647184',
        fontSize: '0.9375rem',
        fontWeight: 500,
        textAlign: 'center',
        padding: theme.spacing(4, 2),
    },
    root: {
        ...interactiveCardStyles,
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        overflow: 'hidden',
        minWidth: 0,
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'center',
        },
    },
    content: {
        flex: '1 1 auto',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(2, 2.5),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    cover: {
        width: '100%',
        height: 140,
        borderRadius: '8px 8px 0 0',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f5f8fc',
        borderBottom: '1px solid #e7ebf0',
        flexShrink: 0,
        '& img': {
            objectFit: 'contain',
        },
        [theme.breakpoints.up('sm')]: {
            width: 112,
            minWidth: 112,
            height: 112,
            margin: theme.spacing(2, 0, 2, 2),
            paddingTop: 0,
            borderRadius: 8,
            borderBottom: 0,
            border: '1px solid #e7ebf0',
            alignSelf: 'center',
            '& img': {
                objectFit: 'contain',
            },
        },
        [theme.breakpoints.up('md')]: {
            width: 136,
            minWidth: 136,
            height: 120,
            marginLeft: 20,
        },
    },
    cardBody: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        minWidth: 0,
        gap: theme.spacing(2),
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(3),
        },
    },
    cardTitle: {
        color: '#647184',
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.3,
        margin: 0,
    },
    cardSubtitle: {
        wordBreak: 'break-word',
        margin: 0,
        color: '#263548',
        '&.MuiTypography-h1': {
            fontSize: '1.125rem',
            lineHeight: 1.35,
            fontWeight: 700,
            paddingTop: 2,
            paddingBottom: 4,
            margin: 0,
            color: '#263548',
        },
    },
    cardDesc: {
        color: '#647184',
        wordBreak: 'break-word',
        fontSize: 14,
        lineHeight: 1.45,
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
    },
    cardContentInner: {
        flex: '1 1 auto',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'center',
    },
    progressBlock: {
        width: '100%',
        marginTop: 12,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        '& .MuiLinearProgress-root': {
            width: '100%',
            height: 8,
            margin: 0,
            borderRadius: 999,
            display: 'block',
        },
        '& .MuiLinearProgress-bar': {
            borderRadius: 999,
        },
    },
    progressLabel: {
        margin: '6px 0 0',
        padding: 0,
        fontSize: 13,
        lineHeight: 1.3,
        color: '#647184',
        fontWeight: 500,
    },
    buttonWrap: {
        width: '100%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.up('sm')]: {
            width: 'auto',
            minWidth: 168,
            justifyContent: 'center',
        },
    },
    launchButton: {
        width: '100%',
        padding: '12px 24px',
        borderRadius: 8,
        maxWidth: '100%',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        textTransform: 'none',
        fontWeight: 600,
        [theme.breakpoints.only('sm')]: {
            padding: '12px 16px',
        },
        [theme.breakpoints.up('md')]: {
            minWidth: 188,
            padding: '12px 28px',
        },
    },
    main: {
        padding: theme.spacing(2, 2),
        boxSizing: 'border-box',
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(2, 0),
        },
    },
    cardGridItem: {
        minWidth: 0,
    },
    tabsRoot: {
        marginBottom: theme.spacing(2),
        borderBottom: '1px solid #e7ebf0',
        minHeight: 44,
        '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#647184',
            minHeight: 44,
        },
        '& .Mui-selected': {
            color: '#263548',
        },
    },
    hubAccessStrip: {
        marginBottom: theme.spacing(2),
    },
    tabIndicator: {
        backgroundColor: theme.palette.primary.main,
        height: 3,
        borderRadius: '3px 3px 0 0',
    },
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        alignItems: 'center',
        marginBottom: theme.spacing(2),
    },
    searchField: {
        flex: '1 1 220px',
        backgroundColor: '#fff',
    },
    pageSizeControl: {
        minWidth: 110,
        backgroundColor: '#fff',
    },
    resultLine: {
        color: '#647184',
        fontSize: '0.8125rem',
        fontWeight: 500,
    },
    paginationRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(2),
    },
    fullWidthCourseWrap: {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    iframeContainer: {
        ...surfaceCardStyles,
        position: 'relative',
        width: '100%',
        height: '100%',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    },
    iframeContainerMaximized: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1400,
        width: '100%',
        height: '100%',
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
    },
    loaderContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1,
        backgroundColor: '#fff',
    },
    courseIframe: {
        border: 'none',
        display: 'block',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#fff',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
    },
}));

const RegisteredCourses = () => {

    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type") || null;
    const isRegisteredCoursesRoute = location.pathname === UserPath.RegisteredCourses;
    const isMyLearningHubCoursesRoute = location.pathname === UserPath.MyLearningHubCourses;
    const activeTab = isMyLearningHubCoursesRoute ? 1 : 0;
    const { userDetails } = useAuthState();
    const impersonation = useImpersonation();
    const isUserRole = (userDetails?.role || USER_ROLES.USER) === USER_ROLES.USER;
    const [showLearningHubTab, setShowLearningHubTab] = React.useState(() => {
        if (impersonation.active) {
            return getImpersonationLearningHubAvailable() === true;
        }
        return isUserRole && isLearningHubAvailable();
    });
    const [registerCourseList, setRegisterCourseList] = React.useState([]);
    const [loader, setLoader] = React.useState(isRegisteredCoursesRoute);
    const [emptyMessage, setEmptyMessage] = React.useState('');

    const [hubCourses, setHubCourses] = React.useState([]);
    const [hubAccess, setHubAccess] = React.useState(() => getLearningHubAccess());
    const [hubLoader, setHubLoader] = React.useState(isMyLearningHubCoursesRoute);
    const [hubLoaded, setHubLoaded] = React.useState(false);
    const [searchInput, setSearchInput] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(0);
    const [perpage, setPerpage] = React.useState(12);

    const [iframeUrl, setIframeUrl] = React.useState('');
    const [isIframeVisible, setIsIframeVisible] = React.useState(false);
    const [iframeLoading, setIframeLoading] = React.useState(false);
    const [isMaximized, setIsMaximized] = React.useState(false);
    const iframeWrapRef = React.useRef(null);
    const { iframeRef: courseIframeRef, handleIframeLoad } = useCourseIframeSelection(
        isIframeVisible,
        iframeUrl
    );

    const openCourseInline = (url) => {
        const launchButton = document.activeElement;
        if (launchButton && launchButton !== document.body && typeof launchButton.blur === 'function') {
            launchButton.blur();
        }
        setIframeUrl(url);
        setIframeLoading(true);
        setIsIframeVisible(true);
        setIsMaximized(false);
    };

    const openCourse = (url, provider) => {
        const launchUrl = courseLaunchUrl(url);
        if (shouldOpenCourseInNewTab(provider)) {
            window.open(launchUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        openCourseInline(launchUrl);
    };

    const closeCourseInline = () => {
        setIsIframeVisible(false);
        setIframeUrl('');
        setIsMaximized(false);
    };

    useMoodleCourseIframeGuard(closeCourseInline);

    React.useEffect(() => {
        setIsIframeVisible(false);
        setIframeUrl('');
        setIsMaximized(false);
    }, [location.pathname, location.key]);

    React.useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    React.useEffect(() => {
        setPage(0);
    }, [search, type, perpage, location.pathname]);

    React.useEffect(() => {
        if (!isUserRole && !impersonation.active) {
            setShowLearningHubTab(false);
            return undefined;
        }

        setShowLearningHubTab(
            impersonation.active
                ? getImpersonationLearningHubAvailable() === true
                : isLearningHubAvailable()
        );

        const handleAvailabilityChange = (event) => {
            setShowLearningHubTab(event.detail.available);
        };
        const handleAccessChange = (event) => {
            setHubAccess(event.detail.access);
        };

        window.addEventListener(LEARNING_HUB_AVAILABILITY_EVENT, handleAvailabilityChange);
        window.addEventListener(LEARNING_HUB_ACCESS_EVENT, handleAccessChange);
        return () => {
            window.removeEventListener(LEARNING_HUB_AVAILABILITY_EVENT, handleAvailabilityChange);
            window.removeEventListener(LEARNING_HUB_ACCESS_EVENT, handleAccessChange);
        };
    }, [isUserRole, impersonation.active]);

    React.useEffect(() => {
        if (!isMyLearningHubCoursesRoute || showLearningHubTab) {
            return;
        }
        if (impersonation.active && getImpersonationLearningHubAvailable() === null) {
            return;
        }
        history.replace(UserPath.RegisteredCourses);
    }, [isMyLearningHubCoursesRoute, showLearningHubTab, impersonation.active, history]);

    React.useEffect(() => {
        if (!isMyLearningHubCoursesRoute || !showLearningHubTab || hubLoaded) {
            return;
        }

        let mounted = true;

        const fetchEnrolledLearningHubCourses = async () => {
            setHubLoader(true);
            const response = await skillsAndTransformation.getEnrolledLearningHubCourses();

            if (!mounted) {
                return;
            }

            if (response?.status) {
                setHubCourses(Array.isArray(response.data) ? response.data : []);
            } else {
                setHubCourses([]);
                notify('error', response?.message || 'Unable to load Learning Hub courses');
            }
            if (response?.access) {
                setLearningHubAccess(response.access);
            } else {
                setHubAccess(getLearningHubAccess());
            }
            setHubLoader(false);
            setHubLoaded(true);
        };

        fetchEnrolledLearningHubCourses();

        return () => {
            mounted = false;
        };
    }, [isMyLearningHubCoursesRoute, showLearningHubTab, hubLoaded]);

    const filteredHubCourses = React.useMemo(() => {
        if (!type) {
            return hubCourses;
        }
        return hubCourses.filter((course) => {
            const score = parseFloat(course.course_completion_score) || 0;
            if (type === "inprogress") {
                return score < 100 && score > 0;
            }
            if (type === "completed") {
                return score >= 100;
            }
            return true;
        });
    }, [hubCourses, type]);

    const hubEmptyMessage = type === "inprogress"
        ? "No Courses In-Progress"
        : type === "completed"
            ? "No Courses Completed"
            : "No Learning Hub Courses Enrolled";

    const searchedRegisteredCourses = React.useMemo(() => (
        registerCourseList.filter((course) => courseMatchesSearch(search, [
            course.subTitle,
            course.title,
            course.provider,
            course.desc,
        ]))
    ), [registerCourseList, search]);

    const searchedHubCourses = React.useMemo(() => (
        filteredHubCourses.filter((course) => courseMatchesSearch(search, [
            course.name,
            course.provider,
            course.description,
        ]))
    ), [filteredHubCourses, search]);

    const registeredPage = sliceCoursePage(searchedRegisteredCourses, page, perpage);
    const hubPage = sliceCoursePage(searchedHubCourses, page, perpage);
    const activePage = activeTab === 1 ? hubPage : registeredPage;

    const courseToolbar = (
        <div className={classes.toolbar}>
            <TextField
                className={classes.searchField}
                variant="outlined"
                size="small"
                placeholder="Search course name"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                inputProps={{ 'aria-label': 'Search course name' }}
            />
            <TextField
                className={classes.pageSizeControl}
                select
                variant="outlined"
                size="small"
                label="Per page"
                value={perpage}
                onChange={(event) => setPerpage(Number(event.target.value))}
            >
                {COURSE_PAGE_SIZES.map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
            </TextField>
            <Typography className={classes.resultLine}>
                {activePage.total
                    ? `${activePage.start + 1}–${activePage.start + activePage.items.length} of ${activePage.total}`
                    : 'No courses'}
            </Typography>
        </div>
    );

    const coursePager = activePage.totalPages > 1 ? (
        <div className={classes.paginationRow}>
            <Pagination
                page={page + 1}
                count={activePage.totalPages}
                onChange={(_event, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
                variant="outlined"
            />
        </div>
    ) : null;

    const handleLaunchHubCourse = (course) => {
        if (course?.goto_url) {
            openCourse(course.goto_url, course.provider);
        } else {
            notify('error', 'Launch URL is not available for this course');
        }
    };

    const handleTabChange = (event, newValue) => {
        const nextPath = newValue === 1 ? UserPath.MyLearningHubCourses : UserPath.RegisteredCourses;

        // Preserve other query params like `type` but remove legacy `tab`.
        const nextParams = new URLSearchParams(location.search);
        nextParams.delete('tab');

        history.push({
            pathname: nextPath,
            search: nextParams.toString() ? `?${nextParams.toString()}` : '',
        });
    };

    const getAllRegisterCourse = React.useCallback(async () => {
        const courseList = await skillsAndTransformation.getRegisterCourse();
        if (courseList.status) {
            setLoader(false);
            const courseArr = [];
            courseList.data?.courses.forEach((cat) => {
                if (cat.category !== 'Work readiness') {
                    cat.data.forEach((item) => {
                        let { course, labs } = item;
                        let obj = {
                            isLab: false,
                            title: course.category,
                            subTitle: course.course_name,
                            desc: course.description,
                            total: 100,
                            value: course.course_completion,
                            width: 12,
                            page: 'registeredCourse',
                            progress: true,
                            provider: course.provider,
                            ...course,
                            image: getCourseImageUrl(course),
                        };
                        if (type === "inprogress" && parseFloat(course.course_completion) < 100 && parseFloat(course.course_completion) > 0) {
                            courseArr.push(obj);
                        } else if (type === "completed" && parseFloat(course.course_completion) >= 100) {
                            courseArr.push(obj);
                        } else if (type === null) {
                            courseArr.push(obj);
                        }
                        if (labs && labs.length) {
                            labs.forEach((course) => {
                                let obj = {
                                    title: course.vendor,
                                    subTitle: course.name,
                                    desc: course.description,
                                    total: 100,
                                    value: course.course_completion,
                                    width: 12,
                                    page: 'registeredCourse',
                                    progress: true,
                                    isLab: true,
                                    provider: course.provider,
                                    ...course,
                                    image: getCourseImageUrl(course),
                                }
                                if (type === "inprogress" && parseFloat(course.course_completion) < 100 && parseFloat(course.course_completion) > 0) {
                                    courseArr.push(obj);
                                } else if (type === "completed" && parseFloat(course.course_completion) >= 100) {
                                    courseArr.push(obj);
                                } else if (type === null) {
                                    courseArr.push(obj);
                                }
                            });
                        }
                    });
                }
            });
            if (type === "inprogress" && !courseArr.length) {
                setEmptyMessage("No Courses In-Progress");
            } else if (type === "completed" && !courseArr.length) {
                setEmptyMessage("No Courses Completed");
            } else {
                setEmptyMessage('');
            }
            setRegisterCourseList(courseArr);
        }
    }, [type]);

    React.useEffect(() => {
        if (!isRegisteredCoursesRoute) {
            return;
        }

        setRegisterCourseList([]);
        setLoader(true);
        getAllRegisterCourse();
    }, [getAllRegisterCourse, isRegisteredCoursesRoute]);

    const handleLinkURL = async (itm) => {
        const { link, course_id_talent_lms, course_id, uuid, category, provider } = itm;
        if (isImpersonating() && !itm.isLab) {
            if (course_id_talent_lms) {
                try {
                    const result = await courseServices.getSessionLinkToCourse(course_id);
                    if (result?.goto_url) {
                        openCourse(result.goto_url, provider);
                    } else {
                        notify('error', 'Launch URL is not found for this course!');
                    }
                } catch (error) {
                    notify('error', 'Launch URL is not found for this course!');
                }
                return;
            }
            if (link) {
                openCourse(link, provider);
                return;
            }
            notify('error', 'Launch URL is not found for this course!');
            return;
        }
        if (itm.isLab && uuid) {
            const results = await courseServices.getLabLaunchURL({ ResourceId: uuid });
            if (results?.data?.Token) {
                openCourse(results?.data?.Token, provider);
            } else {
                notify("error", "Lab Launch URL is not found!");
            }
            return results;
        }
        if (course_id_talent_lms) {
            try {
                courseServices.getSessionLinkToCourse(course_id)
                    .then(({ goto_url }) => {
                        if (goto_url) {
                            openCourse(goto_url, provider);
                        } else {
                            notify("error", "Launch URL is not found for this course!");
                            return null;
                        }
                    })
                    .catch(error => {
                        console.error(error);
                    });
            } catch (error) {
                // Send push notification
                console.error(error);
            }
        } else if (provider === 'Revinova') {
            const resolvedUrl = getSSOAuthUrl(link, 'Deviare', category);
            if (resolvedUrl) {
                openCourse(resolvedUrl, provider);
            } else {
                notify("error", "Launch URL is not found for this course!");
            }
        }
        else {
            const resolvedUrl = getSSOAuthUrl(link, 'simplilearn', category);
            if (resolvedUrl) {
                openCourse(resolvedUrl, provider);
            } else {
                notify("error", "Launch URL is not found for this course!");
            }
        }
    };

    const renderCourseProgress = (value, total) => {
        const percent = calculatePercentage(value, total);
        const ProgressBar = percent === 100 ? BorderLinearProgressComplete : BorderLinearProgress;

        return (
            <div className={classes.progressBlock}>
                <ProgressBar variant="determinate" value={percent} />
                <Typography className={classes.progressLabel} component="div">
                    {`${percent}% completed`}
                </Typography>
            </div>
        );
    };

    return (
        <Layout lockPageScroll={isIframeVisible}>
            {isIframeVisible ? (
                <div className={classes.fullWidthCourseWrap}>
                    <div
                        className={`${classes.iframeContainer}${isMaximized ? ` ${classes.iframeContainerMaximized}` : ''}`}
                        ref={iframeWrapRef}
                    >
                        <CourseIframeToolbar
                            isMaximized={isMaximized}
                            onToggleMaximize={() => setIsMaximized((prev) => !prev)}
                            onClose={closeCourseInline}
                        />
                        {iframeLoading && <AppLoader overlay />}
                        <iframe
                            ref={courseIframeRef}
                            src={iframeUrl}
                            frameBorder="0"
                            title={impersonation.active ? 'Course link' : 'Course'}
                            className={classes.courseIframe}
                            allow="fullscreen; autoplay; picture-in-picture"
                            allowFullScreen
                            onLoad={() => {
                                setIframeLoading(false);
                                handleIframeLoad();
                            }}
                        ></iframe>
                    </div>
                </div>
            ) : (activeTab === 0 ? loader : hubLoader || !hubLoaded) ? (
                <RouteSkeleton variant="course-list" />
            ) : (
                <main className={classes.main}>
                <div className={classes.pageHeader}>
                    <Typography className={classes.pageEyebrow}>My Courses</Typography>
                    <Typography className={classes.pageTitle}>
                        {activeTab === 1 && showLearningHubTab ? 'My Learning Hub Courses' : 'Registered Courses'}
                    </Typography>
                    <Typography className={classes.pageDescription}>
                        {impersonation.active
                            ? OPEN_COURSE_LINK_NOTE
                            : activeTab === 1 && showLearningHubTab
                            ? 'Continue learning from your Learning Hub courses.'
                            : 'Launch and track progress on your registered courses.'}
                    </Typography>
                </div>
                {showLearningHubTab && (
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        className={classes.tabsRoot}
                        classes={{ indicator: classes.tabIndicator }}
                    >
                        <Tab label="Registered Courses" />
                        <Tab label={learningHubMenuLabel(hubAccess)
                            ? `My Learning Hub Courses · ${learningHubMenuLabel(hubAccess)}`
                            : 'My Learning Hub Courses'} />
                    </Tabs>
                )}

                {activeTab === 0 && (
                    <>
                    {courseToolbar}
                    <Grid container spacing={2}>
                        {registeredPage.items.length ? registeredPage.items.map((item) => (
                            <Grid key={item.uuid || item.course_id || item.subTitle} item xs={item.width} className={classes.cardGridItem}>
                                <Card className={classes.root} sx={{ display: "flex" }}>
                                    <LazyCardMedia
                                        className={classes.cover}
                                        image={item.image}
                                        title={item.title}
                                    />
                                    <CardContent className={classes.content}>
                                        <div className={classes.cardBody}>
                                            <div className={classes.cardContentInner}>
                                                <Typography className={classes.cardTitle}>
                                                    {item.isLab ? <b>Lab</b> : item.title}
                                                </Typography>
                                                <Typography variant="h1" className={classes.cardSubtitle}>
                                                    {item.subTitle}
                                                </Typography>
                                                <Typography className={classes.cardDesc} title={item.desc}>
                                                    {item.desc}
                                                </Typography>
                                                {item.progress ? renderCourseProgress(item.value, item.total) : null}
                                            </div>
                                            <div className={classes.buttonWrap}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => handleLinkURL(item)}
                                                    className={classes.launchButton}>
                                                    {courseCardLaunchLabel(item, 'Launch')}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )) : <Grid item xs={12}>
                            <Typography className={classes.emptyState}>
                                {search ? 'No courses match your search.' : (emptyMessage || 'No Course Registered!')}
                            </Typography>
                        </Grid>}
                    </Grid>
                    {coursePager}
                    </>
                )}

                {activeTab === 1 && showLearningHubTab && (
                    <>
                    <LearningHubAccessStrip access={hubAccess} className={classes.hubAccessStrip} />
                    {courseToolbar}
                    <Grid container spacing={2}>
                        {hubPage.items.length ? hubPage.items.map((course) => {
                            const completionScore = parseFloat(course.course_completion_score) || 0;
                            return (
                                <Grid key={course.uuid || course.name} item xs={12} className={classes.cardGridItem}>
                                    <Card className={classes.root} sx={{ display: "flex" }}>
                                        <LazyCardMedia
                                            className={classes.cover}
                                            image={getCourseImageUrl(course)}
                                            title={course.name}
                                        />
                                        <CardContent className={classes.content}>
                                            <div className={classes.cardBody}>
                                                <div className={classes.cardContentInner}>
                                                    {course.provider && (
                                                        <Typography className={classes.cardTitle}>
                                                            {course.provider}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="h1" className={classes.cardSubtitle}>
                                                        {course.name}
                                                    </Typography>
                                                    <Typography className={classes.cardDesc} title={course.description}>
                                                        {course.description || 'Description not available'}
                                                    </Typography>
                                                    {renderCourseProgress(completionScore, 100)}
                                                </div>
                                                <div className={classes.buttonWrap}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleLaunchHubCourse(course)}
                                                        className={classes.launchButton}>
                                                        {impersonation.active ? 'Open course link' : (completionScore > 0 ? "Continue learning" : "Launch")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        }) : hubLoaded ? <Grid item xs={12}>
                            <Typography className={classes.emptyState}>
                                {search ? 'No courses match your search.' : hubEmptyMessage}
                            </Typography>
                        </Grid> : null}
                    </Grid>
                    {coursePager}
                    </>
                )}
                </main>
            )}
        </Layout>
    );
}

export default RegisteredCourses;
